import styled, { css } from "styled-components";

import { ANIMATION, SPACING, SPACING_RESPONSIVE } from "../../utils/constants";
import { ColumnAlign, SortableColumnHeaderVariant } from "./types";
import { BaseButton } from "../Button";

// The one declaration of the column-heading look, shared with the <span> spellings the
// grid-built sections use, so colour, tracking, case and alignment cannot drift between them.
export const columnHeaderCellStyles = css<{ $align?: ColumnAlign }>`
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.06em;
  text-align: ${({ $align }) => $align ?? "start"};
  text-transform: uppercase;
`;

const SECTION_CELL_BOX = css`
  padding: ${SPACING.sm + SPACING.xxs}px ${SPACING.sm}px;
  border-bottom: 1px solid var(--color-card-border);
`;

// The whole section-card heading cell, shared with the plain <th>s that sit in the
// same header row — a change to one would otherwise be a visible split in the other.
export const sectionColumnHeaderCellStyles = css<{ $align?: ColumnAlign }>`
  ${columnHeaderCellStyles}
  ${SECTION_CELL_BOX}
`;

// The two looks this header is asked for: a list page's own table, and the denser
// heading row a section card already uses for its non-sortable columns.
const VARIANT_STYLES: Record<
  SortableColumnHeaderVariant,
  ReturnType<typeof css>
> = {
  list: css`
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
    color: var(--color-text-default);
    border-bottom: 1px solid var(--color-border-light);
  `,
  section: SECTION_CELL_BOX,
};

const JUSTIFY_BY_ALIGN: Record<ColumnAlign, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
};

export const HeaderCell = styled.th<{
  $align?: ColumnAlign;
  $width?: string;
  $variant: SortableColumnHeaderVariant;
}>`
  ${columnHeaderCellStyles}
  ${({ $variant }) => VARIANT_STYLES[$variant]}
  width: ${({ $width }) => $width ?? "auto"};
  white-space: nowrap;
`;

export const SortButton = styled(BaseButton)<{
  $align?: ColumnAlign;
  $variant: SortableColumnHeaderVariant;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: ${({ $align, $variant }) =>
    $align === "end" && $variant === "list"
      ? "var(--color-text-default)"
      : "inherit"};
  justify-content: ${({ $align }) => JUSTIFY_BY_ALIGN[$align ?? "start"]};
  transition: color ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  &:hover {
    color: var(--color-text-default);
  }
`;

// Transparent rather than unmounted, so the header width doesn't shift.
export const SortArrow = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  color: var(--color-text-default);

  & > svg {
    font-size: var(--text-md);
  }
`;
